import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import { Book, FileSpreadsheet, FileText, Pencil, Trash2 } from 'lucide-react';
import { calculateSSSDeduction, calculateTaxDeduction, isEndDateInSSSWindow } from '../../../utils/payrollUtils';
import { computeEffectiveLoanRange, getApplicableLoansForRecord, getLoanDeductionAmountForRecord } from '../../../utils/payrollLoanSchedule';
import { computeMonthWindowFromEndISO, computePrevGrossAndGovFromMonthlyPayrollRecords, normalizeRecordISODate } from '../utils/monthlyGross';
import SavedMfPayslipView, { computeMfDisplay } from '../Shared/SavedMfPayslipView';
import SavedPayrollPayslipView from '../Shared/SavedPayrollPayslipView';

const DatabasePage = ({
  payrollRecords,
  setPayrollRecords,
  payrollEmployees,
  payrollLoans,
  payrollDatabaseTab,
  setPayrollDatabaseTab,
  payrollDatabaseFilters,
  setPayrollDatabaseFilters,
  isLoadingPayrollRecords,
  fetchPayrollRecords,
  fetchPayrollPeriods,
  fetchPayrollRecordsByPeriod,
  fetchAppSettings,
  updateAppSettings,
  fetchMonthlyRecordsForEmployee,
  getUniqueBranches,
  selectedPayrollRecordEmployeeKey,
  setSelectedPayrollRecordEmployeeKey,
  openPayslipModalForEdit,
  deletePayrollRecord,
  deletePayrollRecordsByPeriods,
  recalculatePayrollRecords
}) => {
  const n = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const s = String(v).replace(/,/g, '').trim();
    const x = parseFloat(s);
    return Number.isFinite(x) ? x : 0;
  };
  const displayRecords = payrollRecords.filter(rec => (
    payrollDatabaseTab === 'mf' ? (rec?.deduction_settings?.mf_record === true) : (rec?.deduction_settings?.mf_record !== true)
  ));
  const [isPayrollDetailsOpen, setIsPayrollDetailsOpen] = React.useState(false);
  const [isPayrollPeriodPickerOpen, setIsPayrollPeriodPickerOpen] = React.useState(false);
  const [periodsLoading, setPeriodsLoading] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [periods, setPeriods] = React.useState([]);
  const [selectedPeriod, setSelectedPeriod] = React.useState(null);
  const [isDeletePayrollRegisterOpen, setIsDeletePayrollRegisterOpen] = React.useState(false);
  const [deletePayrollPeriodsLoading, setDeletePayrollPeriodsLoading] = React.useState(false);
  const [deletePayrollPeriods, setDeletePayrollPeriods] = React.useState([]);
  const [selectedDeletePayrollPeriodKeys, setSelectedDeletePayrollPeriodKeys] = React.useState([]);
  const [isDeletingPayrollRegisters, setIsDeletingPayrollRegisters] = React.useState(false);
  const [payslipEnabled, setPayslipEnabled] = React.useState(false);
  const [isPayslipVisibilityOpen, setIsPayslipVisibilityOpen] = React.useState(false);
  const [payslipVisibilityTab, setPayslipVisibilityTab] = React.useState('payroll');
  const [payslipVisibilityPeriods, setPayslipVisibilityPeriods] = React.useState({ payroll: [], mf: [] });
  const [isPayslipVisibilityLoading, setIsPayslipVisibilityLoading] = React.useState(false);
  const [isSavingPayslipVisibility, setIsSavingPayslipVisibility] = React.useState(false);
  const [stagedPayslipVisibility, setStagedPayslipVisibility] = React.useState({ payroll: [], mf: [] });

  React.useEffect(() => {
    setSelectedPeriod(null);
    setPeriods([]);
    setDeletePayrollPeriods([]);
    setSelectedDeletePayrollPeriodKeys([]);
    setIsDeletePayrollRegisterOpen(false);
  }, [payrollDatabaseTab]);

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (typeof fetchAppSettings !== 'function') return;
      const settings = await fetchAppSettings();
      if (!mounted) return;
      setPayslipEnabled(Boolean(settings?.payslip_enabled));
    };
    run();
    return () => {
      mounted = false;
    };
  }, [fetchAppSettings]);

  const buildPeriodKey = React.useCallback((p) => {
    const period = String(p?.payroll_period || '').trim();
    const start = String(p?.start_date || '').trim();
    const end = String(p?.end_date || '').trim();
    return `${period}|${start}|${end}`;
  }, []);

  const selectedDeletePayrollPeriodKeySet = React.useMemo(() => {
    return new Set(selectedDeletePayrollPeriodKeys);
  }, [selectedDeletePayrollPeriodKeys]);

  const selectedDeletePayrollPeriods = React.useMemo(() => {
    if (selectedDeletePayrollPeriodKeySet.size === 0) return [];
    return deletePayrollPeriods.filter((period) => selectedDeletePayrollPeriodKeySet.has(buildPeriodKey(period)));
  }, [deletePayrollPeriods, selectedDeletePayrollPeriodKeySet, buildPeriodKey]);

  const toggleDeletePayrollPeriod = React.useCallback((period, checked) => {
    const key = buildPeriodKey(period);
    setSelectedDeletePayrollPeriodKeys((prev) => {
      if (checked) {
        return prev.includes(key) ? prev : [...prev, key];
      }
      return prev.filter((item) => item !== key);
    });
  }, [buildPeriodKey]);

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!isPayslipVisibilityOpen) return;
      if (typeof fetchAppSettings !== 'function') return;
      if (typeof fetchPayrollPeriods !== 'function') return;

      setIsPayslipVisibilityLoading(true);
      try {
        const [settings, payrollList, mfList] = await Promise.all([
          fetchAppSettings(),
          fetchPayrollPeriods({ source: 'payroll' }),
          fetchPayrollPeriods({ source: 'mf' })
        ]);

        if (!mounted) return;

        setPayslipVisibilityPeriods({
          payroll: Array.isArray(payrollList) ? payrollList : [],
          mf: Array.isArray(mfList) ? mfList : []
        });

        const payrollVisible = Array.isArray(settings?.payslip_visible_payroll_periods)
          ? settings.payslip_visible_payroll_periods
          : [];
        const mfVisible = Array.isArray(settings?.payslip_visible_mf_periods)
          ? settings.payslip_visible_mf_periods
          : [];

        setStagedPayslipVisibility({
          payroll: payrollVisible,
          mf: mfVisible
        });
      } finally {
        if (mounted) setIsPayslipVisibilityLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [isPayslipVisibilityOpen, fetchAppSettings, fetchPayrollPeriods]);

  const stagedPayrollKeySet = React.useMemo(() => {
    const list = stagedPayslipVisibility?.payroll;
    if (!Array.isArray(list)) return new Set();
    return new Set(list.map(buildPeriodKey));
  }, [stagedPayslipVisibility, buildPeriodKey]);

  const stagedMfKeySet = React.useMemo(() => {
    const list = stagedPayslipVisibility?.mf;
    if (!Array.isArray(list)) return new Set();
    return new Set(list.map(buildPeriodKey));
  }, [stagedPayslipVisibility, buildPeriodKey]);

  const toggleStagedVisibility = React.useCallback((source, period, checked) => {
    const src = source === 'mf' ? 'mf' : 'payroll';
    const key = buildPeriodKey(period);
    setStagedPayslipVisibility((prev) => {
      const next = prev && typeof prev === 'object' ? { ...prev } : { payroll: [], mf: [] };
      const currentListRaw = next[src];
      const currentList = Array.isArray(currentListRaw) ? currentListRaw : [];

      if (checked) {
        if (currentList.some((p) => buildPeriodKey(p) === key)) return prev;
        const payrollPeriod = String(period?.payroll_period || '').trim();
        next[src] = [
          ...currentList,
          {
            payroll_period: payrollPeriod ? payrollPeriod : null,
            start_date: String(period?.start_date || '').trim(),
            end_date: String(period?.end_date || '').trim()
          }
        ];
        return next;
      }

      next[src] = currentList.filter((p) => buildPeriodKey(p) !== key);
      return next;
    });
  }, [buildPeriodKey]);

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!isPayrollPeriodPickerOpen) return;
      if (typeof fetchPayrollPeriods !== 'function') return;
      setPeriodsLoading(true);
      try {
        const list = await fetchPayrollPeriods({ source: payrollDatabaseTab });
        if (!mounted) return;
        setPeriods(Array.isArray(list) ? list : []);
      } finally {
        if (mounted) setPeriodsLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [isPayrollPeriodPickerOpen, payrollDatabaseTab, fetchPayrollPeriods]);

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!isDeletePayrollRegisterOpen) return;
      if (typeof fetchPayrollPeriods !== 'function') return;
      setDeletePayrollPeriodsLoading(true);
      try {
        const list = await fetchPayrollPeriods({ source: payrollDatabaseTab });
        if (!mounted) return;
        setDeletePayrollPeriods(Array.isArray(list) ? list : []);
      } finally {
        if (mounted) setDeletePayrollPeriodsLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [isDeletePayrollRegisterOpen, payrollDatabaseTab, fetchPayrollPeriods]);

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const sanitizeFilenamePart = (value) => {
    return String(value || '')
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatMoney = (value) => {
    const safe = n(value);
    if (safe === 0) return '-';
    return safe.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatNumber2 = (value) => {
    const safe = n(value);
    if (safe === 0) return '-';
    return safe.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatInt = (value) => {
    const safe = Math.trunc(n(value));
    if (safe === 0) return '-';
    return safe.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const formatSummaryMoney = (value) => {
    const safe = n(value);
    return safe.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const sumAmounts = (list) => {
    const arr = Array.isArray(list) ? list : [];
    return arr.reduce((sum, item) => sum + n(item?.amount), 0);
  };

  const pad2 = (v) => String(v).padStart(2, '0');

  const formatISO = (date) => {
    if (!date || isNaN(date)) return '';
    const y = date.getFullYear();
    const m = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    return `${y}-${m}-${d}`;
  };

  const hasSavedLoanDeduction = (record) => {
    const loanCycles = Array.isArray(record?.deduction_settings?.loan_cycles) ? record.deduction_settings.loan_cycles : [];
    if (loanCycles.length > 0) {
      const hasEnabled = loanCycles.some((cycle) => {
        if (String(cycle?.cycle_status || '').toLowerCase() === 'skipped') return false;
        const value = cycle?.deducted_amount ?? cycle?.scheduled_amount ?? cycle?.deduct_per_cutoff ?? cycle?.amount;
        return Number(value) > 0;
      });
      if (hasEnabled) return true;
    }
    const topLevelBreakdown = record?.loan_breakdown || record?.deduction_settings?.loan_breakdown;
    if (topLevelBreakdown && typeof topLevelBreakdown === 'object') {
      const sum =
        Number(topLevelBreakdown.company_cash_advance || 0) +
        Number(topLevelBreakdown.company_gadgets || 0) +
        Number(topLevelBreakdown.company_vehicle || 0) +
        Number(topLevelBreakdown.company_others || 0) +
        Number(topLevelBreakdown.government_salary_loan || 0) +
        Number(topLevelBreakdown.government_calamity_loan || 0) +
        Number(topLevelBreakdown.government_hdmf_loan || 0);
      if (sum > 0) return true;
    }
    const list = Array.isArray(record?.additional_deductions) ? record.additional_deductions : [];
    return list.some((ded) => {
      if (ded?.is_loan_deduction === true) return Number(ded?.amount || 0) > 0;
      const hay = `${String(ded?.title || '')} ${String(ded?.description || '')}`.toLowerCase();
      return hay.includes('loan') && Number(ded?.amount || 0) > 0;
    });
  };

  const getPersistedLoanSummary = (record) => {
    const cycles = Array.isArray(record?.deduction_settings?.loan_cycles) ? record.deduction_settings.loan_cycles : [];
    const enabledCycles = cycles.filter((cycle) => String(cycle?.cycle_status || '').toLowerCase() !== 'skipped');
    const loanFormTypes = enabledCycles.map((cycle) => String(cycle?.loan_label || '').trim()).filter(Boolean);
    const loanDeductionPerCutoffTotal = enabledCycles.reduce((sum, cycle) => {
      const value = cycle?.deducted_amount ?? cycle?.scheduled_amount ?? cycle?.deduct_per_cutoff ?? cycle?.amount;
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return sum;
      return sum + n;
    }, 0);
    const breakdownRows =
      (record?.loan_breakdown && Array.isArray(record.loan_breakdown.rows) && record.loan_breakdown.rows) ||
      (record?.deduction_settings?.loan_breakdown && Array.isArray(record.deduction_settings.loan_breakdown.rows) && record.deduction_settings.loan_breakdown.rows) ||
      [];
    const finalRows = enabledCycles.length > 0 ? enabledCycles : breakdownRows;
    return {
      rows: finalRows,
      loanDeductionPerCutoffTotal,
      loanFormTypes
    };
  };

  const computeLoanSummaryForRecord = ({ record }) => {
    return getPersistedLoanSummary(record) || { rows: [], loanDeductionPerCutoffTotal: 0, loanFormTypes: [] };
  };

  const isPersistedGeneratedLoanDeductionItem = (item, record) => {
    if (!item) return false;
    if (item?.is_loan_deduction === true) return true;
    const loanCycles = Array.isArray(record?.deduction_settings?.loan_cycles) ? record.deduction_settings.loan_cycles : [];
    const loanId = String(item?.loan_id || '').trim();
    const knownLoanIds = new Set(loanCycles.map((cycle) => String(cycle?.loan_id || '').trim()).filter(Boolean));
    if (loanId && knownLoanIds.has(loanId)) return true;
    const knownLabels = new Set(
      loanCycles
        .map((cycle) => String(cycle?.loan_label || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const title = String(item?.title || '').trim().toLowerCase();
    const description = String(item?.description || '').trim().toLowerCase();
    if (title.startsWith('loan - ')) {
      const rawLabel = title.slice('loan - '.length).trim();
      if (knownLabels.size === 0 || knownLabels.has(rawLabel)) return true;
    }
    if (description.startsWith('loan deduction for ')) {
      const rawLabel = description.slice('loan deduction for '.length).trim();
      if (knownLabels.size === 0 || knownLabels.has(rawLabel)) return true;
    }
    return false;
  };

  const computePayrollDisplayValues = ({ record }) => {
    const tilesInc = record?.tiles?.income_breakdown || {};
    const tilesDed = record?.tiles?.deductions || {};
    const tilesNet = record?.tiles?.net_pay || {};
    const tileN = (v) => (v !== undefined && v !== null && !Number.isNaN(parseFloat(v)) ? parseFloat(v) : null);
    const nv = (v) => (v !== null && v !== undefined ? Number(v) || 0 : 0);

    const storedSss = record?.deduction_settings?.sss !== false ? (tileN(tilesDed?.sss) ?? n(record?.sss_contribution)) : 0;
    const storedHdmf = record?.deduction_settings?.hdmf !== false ? (tileN(tilesDed?.hdmf) ?? n(record?.hdmf_contribution)) : 0;
    const storedPhic = record?.deduction_settings?.phic !== false ? (tileN(tilesDed?.philhealth) ?? n(record?.philhealth_contribution)) : 0;
    const storedTax = record?.deduction_settings?.tax !== false ? (tileN(tilesDed?.withholding_tax) ?? n(record?.withholding_tax)) : 0;

    const tilesTotalDed = tileN(tilesDed?.total_deductions) ?? tileN(tilesNet?.total_deductions);
    const tilesNetPay = tileN(tilesNet?.net_pay);

    const displayedSss = nv(storedSss);
    const displayedHdmf = nv(storedHdmf);
    const displayedPhic = nv(storedPhic);
    const displayedTax = nv(storedTax);

    const displayTotalDeductions = tilesTotalDed !== null
      ? Math.max(0, tilesTotalDed)
      : Math.max(0, n(record?.total_deductions));

    const displayNetPay = tilesNetPay !== null
      ? Math.max(0, tilesNetPay)
      : Math.max(0, n(record?.net_pay));

    const displayGross = tileN(tilesInc?.gross_salary) ?? tileN(tilesNet?.gross_salary) ?? n(record?.gross_salary);

    const loanDeduction = Number(record?.loan_breakdown?.loan_deduction_per_cutoff_total
      ?? record?.deduction_settings?.loan_breakdown?.loan_deduction_per_cutoff_total
      ?? 0) || 0;

    return {
      displayedSss,
      displayedHdmf,
      displayedPhic,
      displayedTax,
      displayTotalDeductions,
      displayNetPay,
      displayGross,
      loanDeduction
    };
  };

  const computeNormalizedPayrollGross = (record) => {
    const safeRecord = record || {};
    const additionalIncomeTotal = (Array.isArray(safeRecord?.additional_income) ? safeRecord.additional_income : [])
      .reduce((sum, item) => sum + n(item?.amount), 0);
    const overtimePayTotal = (Array.isArray(safeRecord?.overtime_pay) ? safeRecord.overtime_pay : [])
      .reduce((sum, item) => sum + n(item?.amount), 0);
    const otHolidayTotal = n(safeRecord?.ot_pay_amount) + n(safeRecord?.holiday_pay_amount);
    const expectedGrossWithoutOtHoliday = n(safeRecord?.basic_salary) + additionalIncomeTotal + overtimePayTotal;
    const storedGross = n(safeRecord?.gross_salary);
    const grossIncludesOtHoliday = otHolidayTotal > 0
      && Math.abs(storedGross - (expectedGrossWithoutOtHoliday + otHolidayTotal)) <= 0.01;
    return grossIncludesOtHoliday ? expectedGrossWithoutOtHoliday : storedGross;
  };

  const downloadExcelXls = ({ filename, headerRows, bodyRows, meta }) => {
    const safeHeaderRows = Array.isArray(headerRows) ? headerRows : [];
    const safeBodyRows = Array.isArray(bodyRows) ? bodyRows : [];
    const headerColCounts = safeHeaderRows.map((row) =>
      (Array.isArray(row) ? row : []).reduce((sum, cell) => sum + Math.max(1, Number(cell?.colSpan || 1)), 0)
    );
    const dataColCounts = safeBodyRows
      .filter((r) => r?.kind === 'data' && Array.isArray(r?.cells))
      .map((r) => r.cells.length);
    const totalCols = Math.max(0, ...headerColCounts, ...dataColCounts);

    const renderMetaRow = (cells) => {
      const safeCells = Array.isArray(cells) ? cells : [];
      const used = safeCells.reduce((sum, c) => sum + Math.max(1, Number(c?.colSpan || 1)), 0);
      const remaining = Math.max(0, totalCols - used);
      const tds = safeCells
        .map((c) => {
          const span = Math.max(1, Number(c?.colSpan || 1));
          const cls = c?.className ? ` class="${escapeHtml(c.className)}"` : '';
          return `<td colspan="${span}"${cls}>${escapeHtml(c?.value ?? '')}</td>`;
        })
        .join('');
      const filler = remaining > 0 ? `<td colspan="${remaining}"></td>` : '';
      return `<tr>${tds}${filler}</tr>`;
    };

    const periodLabel = String(meta?.payrollPeriod || '').trim();
    const startDate = String(meta?.startDate || '').trim();
    const endDate = String(meta?.endDate || '').trim();
    const companyName = String(meta?.companyName || '').trim();
    const titleSpan = Math.min(4, Math.max(1, totalCols));
    const labelSpan = Math.min(2, Math.max(1, totalCols));
    const valueSpan = Math.min(2, Math.max(1, totalCols - labelSpan));

    const renderHeaderRow = (cells) => {
      const safeCells = Array.isArray(cells) ? cells : [];
      const ths = safeCells
        .map((c) => {
          const colSpan = Math.max(1, Number(c?.colSpan || 1));
          const rowSpan = Math.max(1, Number(c?.rowSpan || 1));
          const cls = c?.className ? ` class="${escapeHtml(c.className)}"` : '';
          const colAttr = colSpan > 1 ? ` colspan="${colSpan}"` : '';
          const rowAttr = rowSpan > 1 ? ` rowspan="${rowSpan}"` : '';
          return `<th${colAttr}${rowAttr}${cls}>${escapeHtml(c?.value ?? '')}</th>`;
        })
        .join('');
      return `<tr>${ths}</tr>`;
    };

    const renderBodyRow = (row) => {
      if (!row) return '';
      if (row.kind === 'branch') {
        return `<tr><td colspan="${totalCols}" class="branch-row">${escapeHtml(String(row.branchName || '').trim())}</td></tr>`;
      }
      if (row.kind === 'spacer') {
        return `<tr><td colspan="${totalCols}">&nbsp;</td></tr>`;
      }
      if (row.kind === 'data') {
        const cells = Array.isArray(row.cells) ? row.cells : [];
        const tds = cells.map((c) => `<td>${escapeHtml(c)}</td>`).join('');
        const filler = cells.length < totalCols ? `<td colspan="${totalCols - cells.length}"></td>` : '';
        return `<tr>${tds}${filler}</tr>`;
      }
      if (row.kind === 'summary') {
        const cells = Array.isArray(row.cells) ? row.cells : [];
        const tds = cells
          .map((cell) => {
            const safeCell = cell && typeof cell === 'object' ? cell : { value: cell };
            const cls = safeCell?.className ? ` class="${escapeHtml(safeCell.className)}"` : '';
            return `<td${cls}>${escapeHtml(safeCell?.value ?? '')}</td>`;
          })
          .join('');
        const filler = cells.length < totalCols
          ? `<td colspan="${totalCols - cells.length}" class="summary-row"></td>`
          : '';
        return `<tr>${tds}${filler}</tr>`;
      }
      return '';
    };

    const spacerRows = Array.from({ length: 4 })
      .map(() => `<tr><td colspan="${totalCols}">&nbsp;</td></tr>`)
      .join('');
    const tableHTML = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Payroll Export</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          body { font-family: Arial, sans-serif; }
          table { border-collapse: collapse; width: 100%; }
          td, th { border: 1px solid #000000; padding: 5px; vertical-align: top; mso-number-format:"\\@"; }
          th { font-weight: bold; background-color: #4472C4; color: #FFFFFF; text-align: center; }
          .meta-title { font-weight: bold; font-size: 14px; text-align: center; }
          .meta-label { font-weight: bold; }
          .branch-row { font-weight: bold; background-color: #D9E1F2; }
          .summary-row { background-color: #FFF2CC; }
          .summary-label { font-weight: bold; }
          .summary-value { text-align: right; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            ${companyName ? renderMetaRow([{ value: companyName, colSpan: titleSpan, className: 'meta-title' }]) : ''}
            ${renderMetaRow([{ value: 'PAYROLL PERIOD', colSpan: labelSpan, className: 'meta-label' }, { value: periodLabel, colSpan: valueSpan }])}
            ${renderMetaRow([{ value: 'Period start date', colSpan: labelSpan, className: 'meta-label' }, { value: startDate, colSpan: valueSpan }])}
            ${renderMetaRow([{ value: 'Period end date', colSpan: labelSpan, className: 'meta-label' }, { value: endDate, colSpan: valueSpan }])}
            ${spacerRows}
            ${safeHeaderRows.map((r) => renderHeaderRow(r)).join('')}
          </thead>
          <tbody>
            ${safeBodyRows.map((r) => renderBodyRow(r)).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob([tableHTML], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExportPayrollPeriod = async () => {
    if (!selectedPeriod?.start_date || !selectedPeriod?.end_date) return;
    if (typeof fetchPayrollRecordsByPeriod !== 'function') return;
    setExportLoading(true);
    try {
      const source = payrollDatabaseTab === 'mf' ? 'mf' : 'payroll';
      const records = await fetchPayrollRecordsByPeriod({
        source,
        start_date: selectedPeriod.start_date,
        end_date: selectedPeriod.end_date
      });
      const safeRecords = Array.isArray(records) ? records : [];

      const headerRows = [
        [
          { value: 'Employee Name', rowSpan: 3 },
          { value: 'Monthly Salary', rowSpan: 3 },
          { value: 'Daily Rate', rowSpan: 3 },
          { value: 'Hourly Rate', rowSpan: 3 },
          { value: 'Minute Rate', rowSpan: 3 },
          { value: 'Half Month Salary', rowSpan: 3 },
          { value: 'Deduction', colSpan: 8 },
          { value: 'Total Deduction', rowSpan: 3 },
          { value: 'Gross Compensation', rowSpan: 3 },
          { value: 'Additions', colSpan: 5 },
          { value: 'Government Deductions', colSpan: 11 },
          { value: 'NET PAY', rowSpan: 3 }
        ],
        [
          { value: 'Absent', colSpan: 2 },
          { value: 'Tardiness', colSpan: 2 },
          { value: 'Undertime', colSpan: 2 },
          { value: 'Adjustment', colSpan: 2 },
          { value: 'Overtime Pay', colSpan: 2 },
          { value: 'Adjustment', colSpan: 2 },
          { value: 'Total Addition', rowSpan: 2 },
          { value: 'W/Tax Compensation', rowSpan: 2 },
          { value: 'Contribution', colSpan: 3 },
          { value: 'Government Loans', colSpan: 3 },
          { value: 'Company Loans', colSpan: 3 },
          { value: 'Total', rowSpan: 2 }
        ],
        [
          { value: 'Per day' },
          { value: 'Amount' },
          { value: 'Per minute' },
          { value: 'Amount' },
          { value: 'Per minute' },
          { value: 'Amount' },
          { value: 'Remarks' },
          { value: 'Amount' },
          { value: 'Hours' },
          { value: 'Amount' },
          { value: 'Remarks' },
          { value: 'Amount' },
          { value: 'SSS' },
          { value: 'PHIC' },
          { value: 'HDMF' },
          { value: 'SSS Salary Loan' },
          { value: 'SSS Calamity Loan' },
          { value: 'HDMF Loan' },
          { value: 'Vehicle Loan' },
          { value: 'Gadget Loan' },
          { value: 'Cash/Other Loans' }
        ]
      ];

      const monthlyBasisCache = new Map();
      const toEndISOForExport = (rec) => {
        const raw = String(rec?.end_date || '').trim();
        if (!raw) return '';
        const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
      };
      const toMonthKeyForExport = (endISO) => {
        const iso = String(endISO || '').trim();
        const m = iso.match(/^(\d{4})-(\d{2})-/);
        return m ? `${m[1]}-${m[2]}` : '';
      };
      const isEndOfMonthISOForExport = (endISO) => {
        const iso = String(endISO || '').trim();
        if (!iso) return false;
        const d = new Date(`${iso}T00:00:00+08:00`);
        if (Number.isNaN(d.getTime())) return false;
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        return d.getDate() === lastDay;
      };
      const getMonthlyBasisForExport = async (record) => {
        const sssShownFallback = n(record?.sss_contribution);
        const taxShownFallback = n(record?.withholding_tax);
        const endISO = normalizeRecordISODate(toEndISOForExport(record));
        const monthKey = toMonthKeyForExport(endISO);
        const empId = String(record?.employee_id || '').trim();
        if (!endISO || !monthKey || !empId) return { sssShown: sssShownFallback, taxShown: taxShownFallback };
        const cacheKey = `${empId}|${monthKey}`;
        if (monthlyBasisCache.has(cacheKey)) return monthlyBasisCache.get(cacheKey);
        if (typeof fetchMonthlyRecordsForEmployee !== 'function') {
          const fallback = { sssShown: sssShownFallback, taxShown: taxShownFallback };
          monthlyBasisCache.set(cacheKey, fallback);
          return fallback;
        }
        try {
          const apiRecords = await fetchMonthlyRecordsForEmployee({ employeeIdCandidates: [empId], endISO });
          const list = (Array.isArray(apiRecords) ? apiRecords : []).filter((r) => toMonthKeyForExport(normalizeRecordISODate(toEndISOForExport(r))) === monthKey);

          const { monthStartISO, monthEndISO } = computeMonthWindowFromEndISO(endISO);
          const selectedStartISO = normalizeRecordISODate(record?.start_date);
          const selectedEndISO = normalizeRecordISODate(record?.end_date);
          const normalizedList = list.map((item) => ({ ...item, gross_salary: computeNormalizedPayrollGross(item) }));
          const { prevGross, prevGov } = computePrevGrossAndGovFromMonthlyPayrollRecords({
            records: normalizedList,
            monthStartISO,
            monthEndISO,
            selectedStartISO,
            selectedEndISO
          });
          const currentGross = computeNormalizedPayrollGross(record);
          const monthlyGross = Math.max(0, (prevGross || 0) + currentGross);

          const sssEnabled = record?.deduction_settings?.sss === true;
          const phicEnabled = record?.deduction_settings?.phic === true;
          const hdmfEnabled = record?.deduction_settings?.hdmf === true;
          const taxEnabled = record?.deduction_settings?.tax === true;
          const isMinimumWage = record?.deduction_settings?.is_minimum_wage === true;

          const computedMonthlySss = sssEnabled ? calculateSSSDeduction(monthlyGross) : 0;
          const currentGov = (phicEnabled ? n(record?.philhealth_contribution) : 0)
            + (hdmfEnabled ? n(record?.hdmf_contribution) : 0)
            + ((sssEnabled && isEndDateInSSSWindow(record?.end_date)) ? computedMonthlySss : 0);
          const monthlyTaxable = Math.max(0, monthlyGross - (prevGov || 0) - currentGov);
          const computedTax = taxEnabled && !isMinimumWage && isEndOfMonthISOForExport(endISO)
            ? calculateTaxDeduction(monthlyTaxable)
            : 0;

          const out = {
            sssShown: sssEnabled && isEndDateInSSSWindow(record?.end_date) ? computedMonthlySss : sssShownFallback,
            taxShown: taxEnabled && isEndOfMonthISOForExport(endISO) ? computedTax : taxShownFallback
          };
          monthlyBasisCache.set(cacheKey, out);
          return out;
        } catch {
          const fallback = { sssShown: sssShownFallback, taxShown: taxShownFallback };
          monthlyBasisCache.set(cacheKey, fallback);
          return fallback;
        }
      };

      const summaryColumnIndexes = new Set([6, 7, 8, 9, 10, 11, 13, 14, 15, 17, 19, 20, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32]);
      const createSummaryAccumulator = () => Array.from({ length: 33 }, () => 0);
      const buildSummaryRow = (label, totals) => ({
        kind: 'summary',
        cells: Array.from({ length: 33 }, (_, idx) => {
          if (idx === 0) return { value: label, className: 'summary-row summary-label' };
          if (summaryColumnIndexes.has(idx)) {
            return { value: formatSummaryMoney(totals[idx]), className: 'summary-row summary-value' };
          }
          return { value: '', className: 'summary-row' };
        })
      });

      const normalized = await Promise.all(safeRecords.map(async (r) => {
        const isMf = payrollDatabaseTab === 'mf';
        const loanSummary = getPersistedLoanSummary(r);
        const d = isMf ? computeMfDisplay(r, loanSummary) : null;

        const branch = String(r?.branch || '').trim() || 'Unassigned';
        const employeeName = String(r?.employee_name || '').trim();
        const halfMonthSalary = isMf ? n(d?.mfAmount) : n(r?.half_month_salary);
        const monthlySalary = halfMonthSalary * 2;

        const dailyRate = n(r?.daily_rate);
        const hourlyRate = n(r?.hourly_rate) || (dailyRate ? dailyRate / 8 : 0);
        const minuteRate = hourlyRate ? hourlyRate / 60 : (dailyRate ? dailyRate / 8 / 60 : 0);

        const absentDays = isMf ? n(d?.absenceDays) : n(r?.days_absent);
        const tardyMinutes = isMf ? n(d?.lateMinutes) : n(r?.minutes_late);
        const undertimeMinutes = isMf ? n(d?.undertimeMinutes) : n(r?.minutes_undertime);

        const absentAmount = isMf ? n(d?.absenceDeduction) : n(r?.absence_deduction);
        const tardyAmount = isMf ? n(d?.tardinessDeduction) : n(r?.tardiness_deduction);
        const undertimeAmount = isMf ? n(d?.undertimeDeduction) : n(r?.undertime_deduction);

        const adjustmentItems = isMf
          ? d?.deductionItems
          : (Array.isArray(r?.additional_deductions)
              ? r.additional_deductions.filter((item) => !isPersistedGeneratedLoanDeductionItem(item, r))
              : []);
        const items = Array.isArray(adjustmentItems) ? adjustmentItems : [];
        const deductionRemarks = items
          .map((it) => String(it?.title || it?.description || '').trim())
          .filter(Boolean)
          .join(', ');
        const adjustmentAmount = sumAmounts(items);

        const totalDeduction = absentAmount + tardyAmount + undertimeAmount + adjustmentAmount;
        const grossCompensation = halfMonthSalary - totalDeduction;

        const overtimeHours = n(r?.ot_hours);
        const overtimeAmount = n(r?.ot_pay_amount);

        const incomeItemsRaw = isMf ? d?.incomeItems : r?.additional_income;
        const incomeItems = Array.isArray(incomeItemsRaw) ? incomeItemsRaw : [];
        const incomeRemarks = incomeItems
          .map((it) => String(it?.title || it?.description || '').trim())
          .filter(Boolean)
          .join(', ');
        const incomeAmount = sumAmounts(incomeItems);
        const totalAddition = overtimeAmount + incomeAmount;

        const withholdingTax = r?.deduction_settings?.tax ? n(r?.withholding_tax) : 0;
        const sssContribution = r?.deduction_settings?.sss ? n(r?.sss_contribution) : 0;
        const phicContribution = r?.deduction_settings?.phic ? n(r?.philhealth_contribution) : 0;
        const hdmfContribution = r?.deduction_settings?.hdmf ? n(r?.hdmf_contribution) : 0;

        const loanRows = Array.isArray(loanSummary?.rows) ? loanSummary.rows : [];
        const getLoanExportDescriptor = (loan) => {
          const type = String(loan?.loan_type || '').trim().toLowerCase();
          const subtype = String(loan?.loan_subtype || '').trim().toLowerCase();
          const label = String(loan?.loan_label || loan?.title || loan?.description || '').trim().toLowerCase();
          const combined = [type, subtype, label].filter(Boolean).join(' ');
          const amount = Number(
            loan?.deducted_amount
            ?? loan?.scheduled_amount
            ?? loan?.deduct_per_cutoff
            ?? loan?.amount
            ?? 0
          );
          return {
            type,
            subtype,
            label,
            combined,
            amount: Number.isFinite(amount) ? amount : 0
          };
        };
        const sumLoansByMatcher = (matcher) => {
          return loanRows.reduce((sum, loan) => {
            const descriptor = getLoanExportDescriptor(loan);
            if (descriptor.amount <= 0) return sum;
            return matcher(descriptor) ? (sum + descriptor.amount) : sum;
          }, 0);
        };

        const sssSalaryLoan = sumLoansByMatcher((loan) => {
          const isGovernment = loan.type === 'government' || loan.label.includes('government');
          return isGovernment && (loan.subtype.includes('salary') || loan.label.includes('salary'));
        });
        const sssCalamityLoan = sumLoansByMatcher((loan) => {
          const isGovernment = loan.type === 'government' || loan.label.includes('government');
          return isGovernment && (loan.subtype.includes('calamity') || loan.label.includes('calamity'));
        });
        const hdmfLoan = sumLoansByMatcher((loan) => {
          const isGovernment = loan.type === 'government' || loan.label.includes('government');
          return isGovernment && (
            loan.subtype.includes('hdmf')
            || loan.subtype.includes('pag-ibig')
            || loan.label.includes('hdmf')
            || loan.label.includes('pag-ibig')
          );
        });

        const vehicleLoan = sumLoansByMatcher((loan) => {
          const isCompany = loan.type === 'company' || loan.label.includes('company');
          return isCompany && (loan.subtype.includes('vehicle') || loan.label.includes('vehicle'));
        });
        const gadgetLoan = sumLoansByMatcher((loan) => {
          const isCompany = loan.type === 'company' || loan.label.includes('company');
          return isCompany && (
            loan.subtype.includes('gadget')
            || loan.subtype.includes('device')
            || loan.label.includes('gadget')
            || loan.label.includes('device')
          );
        });
        const cashOtherLoan = sumLoansByMatcher((loan) => {
          const isCompany = loan.type === 'company' || loan.label.includes('company');
          return isCompany && (
            loan.subtype.includes('cash')
            || loan.subtype.includes('other')
            || loan.label.includes('cash')
            || loan.label.includes('other')
          );
        });

        const payrollDisplay = !isMf
          ? computePayrollDisplayValues({ record: r })
          : null;
        const displayedSss = !isMf ? payrollDisplay?.displayedSss || 0 : 0;
        const displayedTax = !isMf ? payrollDisplay?.displayedTax || 0 : 0;
        const withholdingTaxShown = !isMf ? displayedTax : withholdingTax;
        const sssContributionShown = !isMf ? displayedSss : sssContribution;
        const displayTotalDeductions = !isMf
          ? payrollDisplay?.displayTotalDeductions || 0
          : n(r?.total_deductions);
        const displayNetPay = !isMf
          ? payrollDisplay?.displayNetPay || 0
          : 0;
        const loanDeduction = !isMf ? payrollDisplay?.loanDeduction || 0 : (Number(loanSummary?.loanDeductionPerCutoffTotal) || 0);
        const payslipNetPay = isMf
          ? ((n(r?.net_pay) > 0 ? n(r?.net_pay) : n(d?.netMf)))
          : displayNetPay;
        const payslipTotalDeductions = isMf
          ? (n(r?.total_deductions) > 0 ? n(r?.total_deductions) : Math.max(0, n(d?.additionalDeductionsTotal) + n(d?.loanDeduction)))
          : displayTotalDeductions;
        const summaryValues = createSummaryAccumulator();
        summaryValues[6] = absentDays;
        summaryValues[7] = absentAmount;
        summaryValues[8] = tardyMinutes;
        summaryValues[9] = tardyAmount;
        summaryValues[10] = undertimeMinutes;
        summaryValues[11] = undertimeAmount;
        summaryValues[13] = adjustmentAmount;
        summaryValues[14] = totalDeduction;
        summaryValues[15] = grossCompensation;
        summaryValues[17] = overtimeAmount;
        summaryValues[19] = incomeAmount;
        summaryValues[20] = totalAddition;
        summaryValues[22] = sssContributionShown;
        summaryValues[23] = phicContribution;
        summaryValues[24] = hdmfContribution;
        summaryValues[25] = sssSalaryLoan;
        summaryValues[26] = sssCalamityLoan;
        summaryValues[27] = hdmfLoan;
        summaryValues[28] = vehicleLoan;
        summaryValues[29] = gadgetLoan;
        summaryValues[30] = cashOtherLoan;
        summaryValues[31] = payslipTotalDeductions;
        summaryValues[32] = payslipNetPay;

        return {
          branch,
          employeeName,
          summaryValues,
          cells: [
            employeeName,
            formatMoney(monthlySalary),
            formatMoney(dailyRate),
            formatMoney(hourlyRate),
            formatMoney(minuteRate),
            formatMoney(halfMonthSalary),
            formatInt(absentDays),
            formatMoney(absentAmount),
            formatInt(tardyMinutes),
            formatMoney(tardyAmount),
            formatInt(undertimeMinutes),
            formatMoney(undertimeAmount),
            deductionRemarks,
            formatMoney(adjustmentAmount),
            formatMoney(totalDeduction),
            formatMoney(grossCompensation),
            formatNumber2(overtimeHours),
            formatMoney(overtimeAmount),
            incomeRemarks,
            formatMoney(incomeAmount),
            formatMoney(totalAddition),
            formatMoney(withholdingTaxShown),
            formatMoney(sssContributionShown),
            formatMoney(phicContribution),
            formatMoney(hdmfContribution),
            formatMoney(sssSalaryLoan),
            formatMoney(sssCalamityLoan),
            formatMoney(hdmfLoan),
            formatMoney(vehicleLoan),
            formatMoney(gadgetLoan),
            formatMoney(cashOtherLoan),
            formatMoney(payslipTotalDeductions),
            formatMoney(payslipNetPay)
          ]
        };
      }));

      const byBranchThenName = normalized.sort((a, b) => {
        const bc = String(a.branch || '').localeCompare(String(b.branch || ''), undefined, { sensitivity: 'base' });
        if (bc !== 0) return bc;
        return String(a.employeeName || '').localeCompare(String(b.employeeName || ''), undefined, { sensitivity: 'base' });
      });
      const branchGroups = [];
      let activeGroup = null;
      const grandTotals = createSummaryAccumulator();
      byBranchThenName.forEach((row) => {
        if (!activeGroup || activeGroup.branchName !== row.branch) {
          activeGroup = {
            branchName: row.branch,
            rows: [],
            totals: createSummaryAccumulator()
          };
          branchGroups.push(activeGroup);
        }
        activeGroup.rows.push(row);
        summaryColumnIndexes.forEach((idx) => {
          activeGroup.totals[idx] += n(row?.summaryValues?.[idx]);
          grandTotals[idx] += n(row?.summaryValues?.[idx]);
        });
      });

      const bodyRows = [];
      branchGroups.forEach((group, index) => {
        bodyRows.push({ kind: 'branch', branchName: group.branchName });
        group.rows.forEach((row) => {
          bodyRows.push({ kind: 'data', cells: row.cells });
        });
        bodyRows.push(buildSummaryRow('TOTAL', group.totals));
        if (index < branchGroups.length - 1) {
          bodyRows.push({ kind: 'spacer' });
        }
      });
      if (branchGroups.length > 0) {
        bodyRows.push({ kind: 'spacer' });
        bodyRows.push(buildSummaryRow('GRAND TOTAL', grandTotals));
      }

      downloadExcelXls({
        filename: `${sanitizeFilenamePart(String(selectedPeriod?.payroll_period || '').trim() || `${selectedPeriod.start_date}_to_${selectedPeriod.end_date}`)}.xls`,
        headerRows,
        bodyRows,
        meta: {
          companyName: 'ISOURCE ASIA BUSINESS SOLUTIONS CORPORATION',
          payrollPeriod: String(selectedPeriod?.payroll_period || '').trim(),
          startDate: selectedPeriod.start_date,
          endDate: selectedPeriod.end_date
        }
      });
    } finally {
      setExportLoading(false);
    }
  };

  const normalizeEmployeeName = (name) => String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizeEmployeeId = (id) => {
    const raw = String(id ?? '').trim();
    if (!raw) return '';
    if (/^\d+$/.test(raw)) return raw.replace(/^0+(?=\d)/, '');
    return raw;
  };
  const isEmployeeActive = (employee) => {
    if (!employee) return true;
    if (employee?.is_active === false) return false;
    const status = String(employee?.status || 'active').trim().toLowerCase();
    return status !== 'inactive';
  };
  const toEmployeeKey = (rec) => {
    const normalizedName = normalizeEmployeeName(rec?.employee_name);
    if (normalizedName) return `name:${normalizedName}`;
    const normalizedId = normalizeEmployeeId(rec?.employee_id);
    if (normalizedId) return `id:${normalizedId.toLowerCase()}`;
    return '';
  };
  const getMeaningfulText = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return '';
    if (text.toLowerCase() === 'n/a') return '';
    return text;
  };
  const payrollEmployeeIdentityLookup = React.useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    const byEmail = new Map();
    (Array.isArray(payrollEmployees) ? payrollEmployees : []).forEach((employee) => {
      const normalizedId = normalizeEmployeeId(employee?.employee_id || employee?.id).toLowerCase();
      const normalizedName = normalizeEmployeeName(employee?.full_name || employee?.employee_name || employee?.name);
      const normalizedEmail = String(employee?.email || employee?.employee_email || '').trim().toLowerCase();
      if (normalizedId && !byId.has(normalizedId)) byId.set(normalizedId, employee);
      if (normalizedName && !byName.has(normalizedName)) byName.set(normalizedName, employee);
      if (normalizedEmail && !byEmail.has(normalizedEmail)) byEmail.set(normalizedEmail, employee);
    });
    return { byId, byName, byEmail };
  }, [payrollEmployees]);
  const enrichRecordIdentityFields = React.useCallback((record) => {
    const baseRecord = record || {};
    const normalizedId = normalizeEmployeeId(baseRecord?.employee_id).toLowerCase();
    const normalizedName = normalizeEmployeeName(baseRecord?.employee_name);
    const normalizedEmail = String(baseRecord?.employee_email || '').trim().toLowerCase();
    const matchedEmployee = payrollEmployeeIdentityLookup.byId.get(normalizedId)
      || payrollEmployeeIdentityLookup.byName.get(normalizedName)
      || payrollEmployeeIdentityLookup.byEmail.get(normalizedEmail)
      || null;
    const pick = (...values) => {
      for (const value of values) {
        const text = getMeaningfulText(value);
        if (text) return text;
      }
      return '';
    };
    return {
      ...baseRecord,
      employee_email: pick(baseRecord?.employee_email, matchedEmployee?.email, matchedEmployee?.employee_email),
      branch: pick(baseRecord?.branch, matchedEmployee?.branch) || baseRecord?.branch,
      tin_number: pick(baseRecord?.tin_number, matchedEmployee?.tin_number),
      sss_number: pick(baseRecord?.sss_number, matchedEmployee?.sss_number),
      phic_number: pick(baseRecord?.phic_number, matchedEmployee?.phic_number),
      hdmf_number: pick(baseRecord?.hdmf_number, matchedEmployee?.hdmf_number)
    };
  }, [payrollEmployeeIdentityLookup]);
  const payrollEmployeeStatusLookup = React.useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    (Array.isArray(payrollEmployees) ? payrollEmployees : []).forEach((employee) => {
      const normalizedId = normalizeEmployeeId(employee?.employee_id);
      const normalizedName = normalizeEmployeeName(employee?.full_name || employee?.employee_name);
      const isActive = isEmployeeActive(employee);
      if (normalizedId) byId.set(normalizedId.toLowerCase(), isActive);
      if (normalizedName) byName.set(normalizedName, isActive);
    });
    return { byId, byName };
  }, [payrollEmployees]);
  const parseRecordSortDate = (rec) => {
    const candidates = [
      rec?.end_date,
      rec?.pay_period_end,
      rec?.payroll_period_end,
      rec?.created_at,
      rec?.createdAt,
      rec?.start_date
    ].filter(Boolean);
    for (const c of candidates) {
      const d = new Date(c);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date(0);
  };
  const employees = Array.from(
    displayRecords.reduce((m, rec) => {
      const key = toEmployeeKey(rec);
      if (!key) return m;
      if (!m.has(key)) {
        const normalizedId = normalizeEmployeeId(rec?.employee_id).toLowerCase();
        const normalizedName = normalizeEmployeeName(rec?.employee_name);
        const lookedUpStatus = payrollEmployeeStatusLookup.byId.get(normalizedId)
          ?? payrollEmployeeStatusLookup.byName.get(normalizedName);
        m.set(key, {
          key,
          name: String(rec?.employee_name || '').trim() || String(rec?.employee_id || key),
          employee_id: String(rec?.employee_id ?? '').trim() || null,
          branch: rec?.branch,
          isActive: typeof lookedUpStatus === 'boolean' ? lookedUpStatus : isEmployeeActive(rec)
        });
      }
      return m;
    }, new Map()).values()
  ).sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
  });
  const selectedEmployee = employees.find(e => e.key === selectedPayrollRecordEmployeeKey) || null;
  const selectedEmployeeRecords = selectedEmployee
    ? displayRecords
        .filter(r => toEmployeeKey(r) === selectedEmployee.key)
        .map(enrichRecordIdentityFields)
        .sort((a, b) => parseRecordSortDate(b).getTime() - parseRecordSortDate(a).getTime())
    : [];
  const toEndISO = (rec) => {
    const raw = String(rec?.end_date || '').trim();
    if (!raw) return '';
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
  };
  const toMonthKey = (endISO) => {
    const iso = String(endISO || '').trim();
    if (!iso) return '';
    const m = iso.match(/^(\d{4})-(\d{2})-/);
    return m ? `${m[1]}-${m[2]}` : '';
  };
  const isEndOfMonthISO = (endISO) => {
    const iso = String(endISO || '').trim();
    if (!iso) return false;
    const d = new Date(`${iso}T00:00:00+08:00`);
    if (Number.isNaN(d.getTime())) return false;
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return d.getDate() === lastDay;
  };
  const monthlyRecordsRef = React.useRef(new Map());
  const monthlyRecordsPendingRef = React.useRef(new Set());
  const [, setMonthlyRecordsVersion] = React.useState(0);
  const mfAutoLoadSigRef = React.useRef('');

  const buildMonthCacheKey = (record) => {
    const endISO = toEndISO(record);
    const monthKey = toMonthKey(endISO);
    const empKey = normalizeEmployeeId(record?.employee_id);
    if (!monthKey || !empKey) return '';
    return `${empKey}|${monthKey}`;
  };

  React.useEffect(() => {
    try {
      if (payrollDatabaseTab === 'mf') return;
      if (typeof fetchMonthlyRecordsForEmployee !== 'function') return;
      const list = Array.isArray(selectedEmployeeRecords) ? selectedEmployeeRecords : [];
      const toFetch = [];

      list.forEach((record) => {
        const needsSss = record?.deduction_settings?.sss === true && isEndDateInSSSWindow(record?.end_date);
        const needsTax = record?.deduction_settings?.tax === true && isEndOfMonthISO(toEndISO(record));
        if (!needsSss && !needsTax) return;
        const cacheKey = buildMonthCacheKey(record);
        if (!cacheKey) return;
        if (monthlyRecordsRef.current.has(cacheKey)) return;
        if (monthlyRecordsPendingRef.current.has(cacheKey)) return;
        toFetch.push({ cacheKey, record });
      });

      toFetch.forEach(({ cacheKey, record }) => {
        monthlyRecordsPendingRef.current.add(cacheKey);
        const endISO = toEndISO(record);
        fetchMonthlyRecordsForEmployee({ employeeIdCandidates: [record?.employee_id], endISO })
          .then((records) => {
            monthlyRecordsRef.current.set(cacheKey, Array.isArray(records) ? records : []);
          })
          .catch(() => {
            monthlyRecordsRef.current.set(cacheKey, []);
          })
          .finally(() => {
            monthlyRecordsPendingRef.current.delete(cacheKey);
            setMonthlyRecordsVersion((v) => v + 1);
          });
      });
    } catch {}
  }, [payrollDatabaseTab, selectedPayrollRecordEmployeeKey, displayRecords.length]);


  React.useEffect(() => {
    try {
      if (payrollDatabaseTab !== 'mf') return;
      if (isLoadingPayrollRecords) return;
      if (typeof fetchPayrollRecords !== 'function') return;

      const filters = payrollDatabaseFilters || {};
      const employeeQuery = String(filters.employee_query || '').trim();
      const startDate = String(filters.start_date || '').trim();
      const endDate = String(filters.end_date || '').trim();
      const branch = String(filters.branch || 'all').trim() || 'all';
      const isDefaultFilters = !employeeQuery && !startDate && !endDate && branch === 'all';
      if (!isDefaultFilters) return;

      const sig = `mf|${employeeQuery}|${startDate}|${endDate}|${branch}`;
      if (mfAutoLoadSigRef.current === sig) return;

      const list = Array.isArray(payrollRecords) ? payrollRecords : [];
      const hasMfRecords = list.some((r) => r?.deduction_settings?.mf_record === true);
      mfAutoLoadSigRef.current = sig;
      if (!hasMfRecords) {
        try {
          if (typeof fetchPayrollRecords === 'function') fetchPayrollRecords({ quiet: true });
        } catch {}
      }
    } catch {}
  }, [
    payrollDatabaseTab,
    isLoadingPayrollRecords,
    fetchPayrollRecords,
    payrollRecords,
    payrollDatabaseFilters?.employee_query,
    payrollDatabaseFilters?.start_date,
    payrollDatabaseFilters?.end_date,
    payrollDatabaseFilters?.branch
  ]);
  const getLoadedMonthRecords = ({ record, monthKey, empKey }) => {
    return displayRecords.filter((r) => {
      const rEmp = normalizeEmployeeId(r?.employee_id);
      if (rEmp !== empKey) return false;
      if (rEmp !== empKey) return false;
      const rMonth = toMonthKey(toEndISO(r));
      return rMonth === monthKey;
    });
  };

  const computeMonthlyBasisForRecord = (record) => {
    const endISO = normalizeRecordISODate(toEndISO(record));
    const monthKey = toMonthKey(endISO);
    const empKey = normalizeEmployeeId(record?.employee_id);
    const cacheKey = buildMonthCacheKey(record);
    const apiRecords = cacheKey ? monthlyRecordsRef.current.get(cacheKey) : null;
    const source = Array.isArray(apiRecords) && apiRecords.length > 0 ? 'api' : 'loaded';
    const rawList = source === 'api' ? apiRecords : getLoadedMonthRecords({ record, monthKey, empKey });
    const list = (Array.isArray(rawList) ? rawList : []).filter((r) => toMonthKey(normalizeRecordISODate(toEndISO(r))) === monthKey);

    const { monthStartISO, monthEndISO } = computeMonthWindowFromEndISO(endISO);
    const selectedStartISO = normalizeRecordISODate(record?.start_date);
    const selectedEndISO = normalizeRecordISODate(record?.end_date);
    const normalizedList = list.map((item) => ({ ...item, gross_salary: computeNormalizedPayrollGross(item) }));
    const { prevGross, prevGov } = computePrevGrossAndGovFromMonthlyPayrollRecords({
      records: normalizedList,
      monthStartISO,
      monthEndISO,
      selectedStartISO,
      selectedEndISO
    });
    const currentGross = computeNormalizedPayrollGross(record);
    const monthlyGross = Math.max(0, (prevGross || 0) + currentGross);

    const sssEnabled = record?.deduction_settings?.sss === true;
    const phicEnabled = record?.deduction_settings?.phic === true;
    const hdmfEnabled = record?.deduction_settings?.hdmf === true;
    const taxEnabled = record?.deduction_settings?.tax === true;
    const isMinimumWage = record?.deduction_settings?.is_minimum_wage === true;

    const computedMonthlySss = sssEnabled ? calculateSSSDeduction(monthlyGross) : 0;
    const currentGov = (phicEnabled ? n(record?.philhealth_contribution) : 0)
      + (hdmfEnabled ? n(record?.hdmf_contribution) : 0)
      + ((sssEnabled && isEndDateInSSSWindow(record?.end_date)) ? computedMonthlySss : 0);
    const monthlyTaxable = Math.max(0, monthlyGross - (prevGov || 0) - currentGov);
    const computedTax = taxEnabled && !isMinimumWage && isEndOfMonthISO(endISO)
      ? calculateTaxDeduction(monthlyTaxable)
      : 0;

    const sssShown = sssEnabled && isEndDateInSSSWindow(record?.end_date) && source === 'api' ? computedMonthlySss : n(record?.sss_contribution);
    const taxShown = taxEnabled && isEndOfMonthISO(endISO) && source === 'api' ? computedTax : n(record?.withholding_tax);

    return {
      source,
      sssShown,
      taxShown
    };
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Tabs value={payrollDatabaseTab} onValueChange={setPayrollDatabaseTab}>
              <TabsList>
                <TabsTrigger value="payroll">Payroll Records</TabsTrigger>
                <TabsTrigger value="mf">MF Records</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-200">View and manage saved {payrollDatabaseTab === 'mf' ? 'MF' : 'payroll'} records</p>
      </div>

      <Card className="glass shadow-lg border-0">
        <CardHeader>
          <CardTitle>Search Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Employee Name or ID#</Label>
              <Input
                placeholder="Enter employee name or ID"
                value={payrollDatabaseFilters.employee_query}
                onChange={(e) => setPayrollDatabaseFilters({
                  ...payrollDatabaseFilters,
                  employee_query: e.target.value
                })}
              />
            </div>
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={payrollDatabaseFilters.start_date}
                onChange={(e) => setPayrollDatabaseFilters({
                  ...payrollDatabaseFilters,
                  start_date: e.target.value
                })}
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={payrollDatabaseFilters.end_date}
                onChange={(e) => setPayrollDatabaseFilters({
                  ...payrollDatabaseFilters,
                  end_date: e.target.value
                })}
              />
            </div>
            <div>
              <Label>Branch</Label>
              <Select
                value={payrollDatabaseFilters.branch}
                onValueChange={(value) => setPayrollDatabaseFilters({
                  ...payrollDatabaseFilters,
                  branch: value
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {getUniqueBranches().map(branch => (
                    <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              onClick={fetchPayrollRecords}
              disabled={isLoadingPayrollRecords}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isLoadingPayrollRecords ? 'Loading...' : 'Search Records'}
            </Button>
            <Button
              variant="outline"
              onClick={() => recalculatePayrollRecords?.({ source: payrollDatabaseTab, filters: payrollDatabaseFilters })}
              disabled={
                isLoadingPayrollRecords ||
                !payrollDatabaseFilters.start_date ||
                !payrollDatabaseFilters.end_date ||
                displayRecords.length === 0
              }
            >
              Recalculate & Update Records
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setPayrollDatabaseFilters({
                  employee_query: '',
                  start_date: '',
                  end_date: '',
                  branch: 'all'
                });
                setPayrollRecords([]);
              }}
            >
              Clear Filters
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsPayrollDetailsOpen(true)}
              disabled={isLoadingPayrollRecords}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Payroll Register
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsDeletePayrollRegisterOpen(true)}
              disabled={isLoadingPayrollRecords}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Payroll Register
            </Button>
            <div className="flex items-center gap-2 px-3 border rounded-md">
              <span className="text-sm">Payslip</span>
              <Switch
                checked={payslipEnabled}
                onCheckedChange={async (checked) => {
                  const next = Boolean(checked);
                  const prev = payslipEnabled;
                  setPayslipEnabled(next);
                  if (typeof updateAppSettings !== 'function') return;
                  const res = await updateAppSettings({ payslip_enabled: next });
                  if (!res) setPayslipEnabled(prev);
                }}
              />
              {payslipEnabled ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setIsPayslipVisibilityOpen(true)}
                >
                  <Book className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isPayslipVisibilityOpen} onOpenChange={setIsPayslipVisibilityOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payslip Visibility</DialogTitle>
            <DialogDescription className="sr-only">Manage payslip visibility settings</DialogDescription>
          </DialogHeader>

          <Tabs value={payslipVisibilityTab} onValueChange={setPayslipVisibilityTab}>
            <TabsList>
              <TabsTrigger value="payroll">Payroll</TabsTrigger>
              <TabsTrigger value="mf">MF</TabsTrigger>
            </TabsList>

            <TabsContent value="payroll">
              <div className="max-h-[55vh] overflow-y-auto space-y-2 mt-2">
                {isPayslipVisibilityLoading ? (
                  <div className="py-8 text-center text-sm text-gray-500">Loading...</div>
                ) : payslipVisibilityPeriods?.payroll?.length ? (
                  payslipVisibilityPeriods.payroll.map((p, idx) => {
                    const key = buildPeriodKey(p);
                    const checked = stagedPayrollKeySet.has(key);
                    return (
                      <div key={`${key}_${idx}`} className="flex items-start gap-3 p-2 border rounded-md">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => toggleStagedVisibility('payroll', p, Boolean(value))}
                        />
                        <div className="text-sm">
                          <div className="font-medium">
                            {String(p?.payroll_period || '').trim() || 'Not set'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {p?.start_date} to {p?.end_date}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="py-8 text-center text-sm text-gray-500">No payroll periods found.</div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="mf">
              <div className="max-h-[55vh] overflow-y-auto space-y-2 mt-2">
                {isPayslipVisibilityLoading ? (
                  <div className="py-8 text-center text-sm text-gray-500">Loading...</div>
                ) : payslipVisibilityPeriods?.mf?.length ? (
                  payslipVisibilityPeriods.mf.map((p, idx) => {
                    const key = buildPeriodKey(p);
                    const checked = stagedMfKeySet.has(key);
                    return (
                      <div key={`${key}_${idx}`} className="flex items-start gap-3 p-2 border rounded-md">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => toggleStagedVisibility('mf', p, Boolean(value))}
                        />
                        <div className="text-sm">
                          <div className="font-medium">
                            {String(p?.payroll_period || '').trim() || 'Not set'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {p?.start_date} to {p?.end_date}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="py-8 text-center text-sm text-gray-500">No MF periods found.</div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPayslipVisibilityOpen(false)}
              disabled={isSavingPayslipVisibility}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={async () => {
                if (typeof updateAppSettings !== 'function') return;
                setIsSavingPayslipVisibility(true);
                try {
                  const payload = {
                    payslip_visible_payroll_periods: Array.isArray(stagedPayslipVisibility?.payroll)
                      ? stagedPayslipVisibility.payroll
                      : [],
                    payslip_visible_mf_periods: Array.isArray(stagedPayslipVisibility?.mf)
                      ? stagedPayslipVisibility.mf
                      : []
                  };
                  const res = await updateAppSettings(payload);
                  if (res) setIsPayslipVisibilityOpen(false);
                } finally {
                  setIsSavingPayslipVisibility(false);
                }
              }}
              disabled={isPayslipVisibilityLoading || isSavingPayslipVisibility}
            >
              {isSavingPayslipVisibility ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isPayrollDetailsOpen} onOpenChange={setIsPayrollDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payroll Details</DialogTitle>
            <DialogDescription className="sr-only">Payroll register details and options</DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-2">
            <img
              src={`${process.env.PUBLIC_URL}/isource_logo.png`}
              alt="iSource logo"
              className="h-20 w-40 object-contain"
            />
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Payroll Priod</Label>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between"
                onClick={() => setIsPayrollPeriodPickerOpen(true)}
              >
                <span className={selectedPeriod ? '' : 'text-gray-500'}>
                  {selectedPeriod
                    ? `${String(selectedPeriod?.payroll_period || '').trim() || 'Not set'} | ${selectedPeriod.start_date} to ${selectedPeriod.end_date}`
                    : 'Select Payroll Priod'}
                </span>
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Period Start Date</Label>
              <Input readOnly value={selectedPeriod?.start_date || ''} />
            </div>

            <div className="space-y-2">
              <Label>Period End Date</Label>
              <Input readOnly value={selectedPeriod?.end_date || ''} />
            </div>

            <div className="pt-2">
              <Button
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={!selectedPeriod || exportLoading}
                onClick={handleExportPayrollPeriod}
              >
                {exportLoading ? 'Exporting...' : 'Export Payroll'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isPayrollPeriodPickerOpen} onOpenChange={setIsPayrollPeriodPickerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Payroll Priod</DialogTitle>
            <DialogDescription className="sr-only">Select a payroll period</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {periodsLoading ? (
              <div className="py-8 text-center text-sm text-gray-500">Loading...</div>
            ) : periods.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">No payroll periods found.</div>
            ) : (
              periods.map((p, idx) => (
                <Button
                  key={`${p?.start_date || 'start'}_${p?.end_date || 'end'}_${idx}`}
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    setSelectedPeriod(p);
                    setIsPayrollPeriodPickerOpen(false);
                  }}
                >
                  <div className="flex w-full items-center justify-between">
                    <div className="text-left">
                      <div className="font-medium">
                        {String(p?.payroll_period || '').trim() || 'Not set'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {p?.start_date} to {p?.end_date}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {typeof p?.count === 'number' ? `${p.count} record(s)` : ''}
                    </div>
                  </div>
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeletePayrollRegisterOpen}
        onOpenChange={(open) => {
          setIsDeletePayrollRegisterOpen(open);
          if (!open) {
            setSelectedDeletePayrollPeriodKeys([]);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Delete Payroll Register</DialogTitle>
            <DialogDescription className="sr-only">Delete payroll register confirmation</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Select one or more saved {payrollDatabaseTab === 'mf' ? 'MF payslip periods' : 'payroll periods'} to delete all
              {' '}
              {payrollDatabaseTab === 'mf' ? 'MF payslips' : 'payslips'}
              {' '}under those periods.
            </p>

            <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
              {deletePayrollPeriodsLoading ? (
                <div className="py-8 text-center text-sm text-gray-500">Loading...</div>
              ) : deletePayrollPeriods.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  No saved {payrollDatabaseTab === 'mf' ? 'MF' : 'payroll'} periods found.
                </div>
              ) : (
                deletePayrollPeriods.map((period, idx) => {
                  const key = buildPeriodKey(period);
                  const checked = selectedDeletePayrollPeriodKeySet.has(key);
                  return (
                    <div key={`${key}_${idx}`} className="flex items-start gap-3 rounded-md border p-3">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => toggleDeletePayrollPeriod(period, Boolean(value))}
                      />
                      <div className="min-w-0 flex-1 text-sm">
                        <div className="font-medium">
                          {String(period?.payroll_period || '').trim() || 'Not set'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {period?.start_date} to {period?.end_date}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {typeof period?.count === 'number' ? `${period.count} payslip(s)` : ''}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <p className="text-sm text-gray-500">
                {selectedDeletePayrollPeriods.length} period(s) selected
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDeletePayrollRegisterOpen(false)}
                  disabled={isDeletingPayrollRegisters}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-red-600 hover:bg-red-700"
                  disabled={
                    deletePayrollPeriodsLoading ||
                    isDeletingPayrollRegisters ||
                    selectedDeletePayrollPeriods.length === 0 ||
                    typeof deletePayrollRecordsByPeriods !== 'function'
                  }
                  onClick={async () => {
                    if (typeof deletePayrollRecordsByPeriods !== 'function') return;
                    setIsDeletingPayrollRegisters(true);
                    try {
                      const ok = await deletePayrollRecordsByPeriods({
                        source: payrollDatabaseTab,
                        periods: selectedDeletePayrollPeriods
                      });
                      if (ok) {
                        setSelectedDeletePayrollPeriodKeys([]);
                        setIsDeletePayrollRegisterOpen(false);
                      }
                    } finally {
                      setIsDeletingPayrollRegisters(false);
                    }
                  }}
                >
                  {isDeletingPayrollRegisters ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div key={payrollDatabaseTab} className="animate-in fade-in slide-in-from-bottom-2 duration-300 ease-in-out">
        {isLoadingPayrollRecords ? (
          <Card className="glass shadow-lg border-0">
            <CardContent className="py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Loading Payroll Records</h3>
                <p className="text-gray-500 dark:text-gray-300">Fetching saved payroll data...</p>
              </div>
            </CardContent>
          </Card>
        ) : displayRecords.length === 0 ? (
          <Card className="glass shadow-lg border-0">
            <CardContent className="py-12">
              <div className="text-center">
                <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No {payrollDatabaseTab === 'mf' ? 'MF' : 'Payroll'} Records Found</h3>
                <p className="text-gray-500 dark:text-gray-300">No saved {payrollDatabaseTab === 'mf' ? 'MF' : 'payroll'} records match your search criteria</p>
                <Button onClick={fetchPayrollRecords} className="mt-4 bg-indigo-600 hover:bg-indigo-700">Load All Records</Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-200">Total Records Found</p>
                    <p className="text-3xl font-bold text-indigo-900">{displayRecords.length}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-200">Total Net Pay</p>
                    <p className="text-2xl font-bold text-green-700">
                      ₱{displayRecords.reduce((sum, record) => {
                        if (payrollDatabaseTab === 'mf') {
                          const mfTilesNet = record?.tiles?.net_pay?.net_pay;
                          const mfNetNum = mfTilesNet !== undefined && mfTilesNet !== null
                            ? parseFloat(String(mfTilesNet).replace(/,/g, ''))
                            : NaN;
                          if (!Number.isNaN(mfNetNum) && Number.isFinite(mfNetNum)) {
                            return sum + mfNetNum;
                          }
                          const mfFallback = n(record?.net_pay);
                          if (mfFallback > 0) return sum + mfFallback;
                          const loanSummary = getPersistedLoanSummary(record);
                          return sum + computeMfDisplay(record, loanSummary).netMf;
                        }
                        const tilesNet = record?.tiles?.net_pay?.net_pay;
                        const tilesNetNum = tilesNet !== undefined && tilesNet !== null
                          ? parseFloat(String(tilesNet).replace(/,/g, ''))
                          : NaN;
                        if (!Number.isNaN(tilesNetNum) && Number.isFinite(tilesNetNum)) {
                          return sum + tilesNetNum;
                        }
                        const fallback = n(record?.net_pay);
                        return sum + Math.max(0, fallback);
                      }, 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <Card className="glass shadow-lg border-0 lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Employees</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[60vh] overflow-y-auto">
                    {employees.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-300">No employees found.</div>
                    ) : (
                      <div className="space-y-1 p-2">
                        {employees.map((emp, idx) => {
                          const isSelected = emp.key === selectedPayrollRecordEmployeeKey;
                          const prevEmployee = idx > 0 ? employees[idx - 1] : null;
                          const startsStatusSection = !prevEmployee || prevEmployee.isActive !== emp.isActive;
                          return (
                            <React.Fragment key={emp.key}>
                              {startsStatusSection && (
                                <div className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                  {emp.isActive ? 'Active Accounts' : 'Inactive Accounts'}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => setSelectedPayrollRecordEmployeeKey(emp.key)}
                                className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                  emp.isActive
                                    ? (
                                      isSelected
                                        ? 'bg-emerald-50 text-emerald-700 border-l-2 border-emerald-500'
                                        : 'text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-black'
                                    )
                                    : (
                                      isSelected
                                        ? 'bg-gray-100 text-gray-500 border-l-2 border-gray-400'
                                        : 'text-gray-400 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-black'
                                    )
                                }`}
                              >
                                <div className="font-medium truncate">{emp.name}</div>
                                <div className={`text-xs truncate ${emp.isActive ? 'text-gray-500 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                                  {emp.employee_id ? `ID: ${emp.employee_id}` : ''}{emp.employee_id && emp.branch ? ' • ' : ''}{emp.branch || ''}
                                </div>
                              </button>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass shadow-lg border-0 lg:col-span-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Payslips{selectedEmployee ? ` — ${selectedEmployee.name}` : ''}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {!selectedEmployee ? (
                    <div className="py-10 text-center">
                      <p className="text-gray-500 dark:text-gray-300">Select an employee to view payslips.</p>
                    </div>
                  ) : selectedEmployeeRecords.length === 0 ? (
                    <div className="py-10 text-center">
                      <p className="text-gray-500 dark:text-gray-300">No payslips available for this employee.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto pr-2">
                      {selectedEmployeeRecords.map((record) => (
                        (() => {
                          if (payrollDatabaseTab === 'mf') {
                            return (
                              <Card key={record.id} className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300 hover:shadow-xl transition-shadow">
                                <CardContent className="pt-6 p-6">
                                  <SavedMfPayslipView
                                    record={record}
                                    className="bg-white p-0 border-0 rounded-none"
                                  />
                                  <div className="flex justify-end space-x-2 pt-4 border-t">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-blue-600 hover:text-blue-700"
                                      onClick={() => openPayslipModalForEdit(record)}
                                    >
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Edit
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => deletePayrollRecord(record.id)}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          }

                          return (
                        <Card key={record.id} className="glass shadow-lg border-0 hover:shadow-xl transition-shadow">
                          <CardContent className="p-6">
                            <SavedPayrollPayslipView
                              record={record}
                              className="bg-white p-0 border-0 rounded-none"
                            />

                            <div className="flex justify-end space-x-2 pt-4 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700"
                                onClick={() => openPayslipModalForEdit(record)}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => deletePayrollRecord(record.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                          );
                        })()
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabasePage;
